import { schreibeBase64 } from './dateien';
import { base64 } from './webcrypto';
import { Aufnahmelauf, type Laufzustand } from './videoLauf';
/*
 * ⚠️ **Nur der Typ, kein Wert.** `video.ts` bindet dieses Modul ein; ein Wert von dort zurück
 * wäre ein echter Ringschluss. Die eine Regel, die hier gebraucht wird — wann das Standbild
 * gezogen wird —, kommt deshalb als Parameter herein (`vorschauZeit`) und nicht als Import.
 */
import type { VideoResult } from './video';

/**
 * Videoaufnahme im Browser.
 *
 * ⚠️ **Bis zum 2026-09-22 warf der Rückfall.** Die Begründung war „Aufnehmen kann er nicht" —
 * das stimmt so nicht: `getUserMedia` und `MediaRecorder` können es, und Chrome schreibt
 * unmittelbar `video/mp4;codecs=avc1`, also dasselbe Format, das vom Gerät kommt. Gemessen am
 * 2026-09-22 in Chrome 153.
 *
 * ⭐ **Der `VideoSegmentMerger` entfällt hier vollständig.** Nativ entsteht je Pause eine
 * eigene Datei, die hinterher zusammengeschnitten werden muss; `MediaRecorder` kennt
 * `pause()`/`resume()` und schreibt alles in **einen** Strom. Die Zahl der Teilstücke wird
 * trotzdem geführt — sie steht im Protokoll und soll dort dasselbe bedeuten.
 *
 * ⚠️ **Die Bühne ist reines DOM.** Ein Capacitor-Plugin darf nichts aus `features/` ziehen;
 * die Richtung der Abhängigkeiten drehte sich sonst um. Nativ ist es ein eigener
 * `UIViewController`, hier ein `<div>` über allem.
 */

/** Was der Browser zur Aufnahme beisteuert — im Test eine Attrappe. */
export interface Aufnahmequelle {
  /** Der Strom für die Vorschau. `null` heisst: keine Vorschau (Test). */
  strom: MediaStream | null;
  starten(): void;
  pausieren(): void;
  fortsetzen(): void;
  /** Beendet die Aufnahme und gibt die Bytes. */
  beenden(): Promise<Blob>;
  /** Abbruch: alles wegwerfen, nichts zurückgeben. */
  verwerfen(): void;
  /** Der Ton lässt sich mitten im Lauf umlegen — die Spur wird stumm, nicht entfernt. */
  ton(an: boolean): void;
}

/** Was der Prüfer auf der Bühne tun kann. */
export interface BedienHorcher {
  start(): void;
  pause(): void;
  weiter(): void;
  fertig(): void;
  abbruch(): void;
  ton(an: boolean): void;
}

/** Die Bühne selbst — im Test eine Attrappe, die einfach Knöpfe drückt. */
export interface Bedienung {
  oeffne(optionen: { mitTon: boolean; strom: MediaStream | null; horcher: BedienHorcher }): void;
  /** Bei jedem Takt: Zustand und Restzeit. */
  zeige(zustand: Laufzustand, restMs: number): void;
  schliesse(): void;
}

export interface VideoWebDeps {
  quelle?: (mitTon: boolean) => Promise<Aufnahmequelle>;
  bedienung?: () => Bedienung;
  /** Schreibt unterhalb von Documents. */
  schreibe?: (pfad: string, inhalt: string) => Promise<void>;
  /** Zieht das Standbild aus den Bytes; `null`, wenn es nicht gelang. */
  standbild?: (bytes: Blob, ms: number) => Promise<string | null>;
  jetzt?: () => number;
  /** Wie oft die Restzeit nachgerechnet wird. */
  taktMs?: number;
  kennung?: () => string;
}

/** Was `nimmAufImBrowser` fertig zusammengesetzt bekommt. */
export interface VideoWebTeile {
  quelle: (mitTon: boolean) => Promise<Aufnahmequelle>;
  bedienung: () => Bedienung;
  schreibe: (pfad: string, inhalt: string) => Promise<void>;
  standbild: (bytes: Blob, ms: number) => Promise<string | null>;
  jetzt: () => number;
  taktMs: number;
  kennung: () => string;
  /** Wann das Standbild gezogen wird — `video.ts::vorschauZeitMs`, hereingereicht. */
  vorschauZeit: (durationSeconds: number) => number;
}

/**
 * Die Formate in der Reihenfolge, in der wir sie wollen.
 *
 * ⚠️ **mp4 zuerst, und zwar mit Nachdruck.** Kommt webm beim Server an, liegen in derselben
 * Ablage zwei Formate nebeneinander, und jeder Leser muss beide vertragen. Chrome kann mp4;
 * ein Browser, der es nicht kann, ist nicht unser Zielbrowser
 * (`docs/plan-web-rueckfaelle.md`).
 */
export const TYPEN: { typ: string; endung: string }[] = [
  { typ: 'video/mp4;codecs=avc1', endung: 'mp4' },
  { typ: 'video/mp4', endung: 'mp4' },
  { typ: 'video/webm;codecs=vp9', endung: 'webm' },
  { typ: 'video/webm', endung: 'webm' },
];

/** Der erste Typ, den dieser Browser wirklich schreiben kann. */
export function waehleTyp(
  kann: (typ: string) => boolean,
  typen: { typ: string; endung: string }[] = TYPEN,
): { typ: string; endung: string } | null {
  return typen.find((k) => kann(k.typ)) ?? null;
}

/** Wohin die frische Aufnahme fällt, bevor `captureVideo` sie an ihren Platz schiebt. */
export const TMP_ORDNER = 'tmp';

export const tmpPfad = (kennung: string, endung: string) =>
  TMP_ORDNER + '/' + kennung + '.' + endung;

export const tmpThumbPfad = (kennung: string) => TMP_ORDNER + '/' + kennung + '-thumb.jpg';

/* ------------------------------------------------------------------ Die echten Teile */

async function browserQuelle(mitTon: boolean): Promise<Aufnahmequelle> {
  const gewaehlt = waehleTyp((t) => MediaRecorder.isTypeSupported(t));
  if (!gewaehlt) throw new Error('Dieser Browser kann kein Video aufnehmen.');

  const strom = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
  // ⚠️ Die Tonspur wird **stummgeschaltet, nicht entfernt**: ein Strom ohne Tonspur liesse
  // sich während der Aufnahme nicht mehr um eine ergänzen, und der Schalter auf der Bühne
  // soll bis zuletzt wirken.
  for (const spur of strom.getAudioTracks()) spur.enabled = mitTon;

  const recorder = new MediaRecorder(strom, { mimeType: gewaehlt.typ });
  const stuecke: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) stuecke.push(e.data);
  };

  const aufraeumen = () => {
    for (const spur of strom.getTracks()) spur.stop();
  };

  return {
    strom,
    starten: () => recorder.start(),
    pausieren: () => recorder.pause(),
    fortsetzen: () => recorder.resume(),
    ton: (an) => {
      for (const spur of strom.getAudioTracks()) spur.enabled = an;
    },
    beenden: () =>
      new Promise<Blob>((gelingt) => {
        recorder.onstop = () => {
          aufraeumen();
          gelingt(new Blob(stuecke, { type: gewaehlt.typ }));
        };
        if (recorder.state === 'inactive') recorder.onstop(new Event('stop'));
        else recorder.stop();
      }),
    verwerfen: () => {
      if (recorder.state !== 'inactive') recorder.stop();
      aufraeumen();
    },
  };
}

/**
 * Das Standbild aus den Bytes — wie nativ bei `vorschauZeitMs`, nicht beim ersten Einzelbild.
 *
 * ⚠️ **Dieselbe Regel wie `VideoThumbnail.swift`.** Weicht sie ab, läge die Markierung der
 * Vorschaukachel neben dem Bild (s. `features/comment/VideoThumb.tsx`).
 */
async function browserStandbild(bytes: Blob, ms: number): Promise<string | null> {
  const adresse = URL.createObjectURL(bytes);
  try {
    const bild = await new Promise<HTMLCanvasElement | null>((fertig) => {
      const el = document.createElement('video');
      el.muted = true;
      el.playsInline = true;
      el.preload = 'auto';
      el.onloadeddata = () => {
        el.currentTime = Math.min(ms / 1000, el.duration || 0);
      };
      el.onseeked = () => {
        const leinwand = document.createElement('canvas');
        leinwand.width = el.videoWidth;
        leinwand.height = el.videoHeight;
        leinwand.getContext('2d')?.drawImage(el, 0, 0);
        fertig(leinwand);
      };
      el.onerror = () => fertig(null);
      el.src = adresse;
    });
    if (!bild || bild.width === 0) return null;
    // Nur die Nutzlast: `saveBase64` und Freunde erwarten sie ohne den `data:`-Vorsatz.
    return bild.toDataURL('image/jpeg', 0.8).split(',')[1] ?? null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(adresse);
  }
}

/* ------------------------------------------------------------------ Die Aufnahme */

/**
 * Nimmt auf und legt die Bytes in `tmp/` ab.
 *
 * Herausgelöst aus dem Plugin, damit sich der Ablauf mit Attrappen prüfen lässt: wann ein
 * Teilstück dazukommt, wann die Grenze greift, was ein Abbruch zurücklässt.
 */
export async function nimmAufImBrowser(
  optionen: { maxSeconds: number; mitTon: boolean },
  deps: VideoWebTeile,
  endung: string,
): Promise<VideoResult> {
  const quelle = await deps.quelle(optionen.mitTon);
  const bedienung = deps.bedienung();
  const lauf = new Aufnahmelauf(Math.max(optionen.maxSeconds, 1) * 1000);

  let takt: ReturnType<typeof setInterval> | null = null;
  const haltTakt = () => {
    if (takt !== null) clearInterval(takt);
    takt = null;
  };

  const ausgang = await new Promise<'fertig' | 'abbruch'>((entschieden) => {
    const fertig = () => {
      if (lauf.zustand === 'fertig') return;
      lauf.beende(deps.jetzt());
      haltTakt();
      entschieden('fertig');
    };

    const horcher: BedienHorcher = {
      start: () => {
        lauf.start(deps.jetzt());
        quelle.starten();
        takt = setInterval(() => {
          const t = deps.jetzt();
          bedienung.zeige(lauf.zustand, lauf.restMs(t));
          // Die Grenze beendet von selbst — der Prüfer muss nicht mitzählen.
          if (lauf.istVoll(t)) fertig();
        }, deps.taktMs);
      },
      pause: () => {
        lauf.pause(deps.jetzt());
        quelle.pausieren();
      },
      weiter: () => {
        lauf.weiter(deps.jetzt());
        quelle.fortsetzen();
      },
      fertig,
      abbruch: () => {
        haltTakt();
        entschieden('abbruch');
      },
      ton: (an) => quelle.ton(an),
    };

    bedienung.oeffne({ mitTon: optionen.mitTon, strom: quelle.strom, horcher });
    bedienung.zeige(lauf.zustand, lauf.restMs(deps.jetzt()));
  });

  haltTakt();

  try {
    if (ausgang === 'abbruch') {
      quelle.verwerfen();
      return { cancelled: true };
    }

    const bytes = await quelle.beenden();
    const kennung = deps.kennung();
    const pfad = tmpPfad(kennung, endung);
    await deps.schreibe(pfad, base64(new Uint8Array(await bytes.arrayBuffer())));

    const dauerMs = lauf.dauerMs(deps.jetzt());
    // Kein Grund zum Abbruch: ohne Standbild zeigt die Liste weiter ihr Zeichen.
    const standbild = await deps
      .standbild(bytes, deps.vorschauZeit(Math.round(dauerMs / 1000)))
      .catch(() => null);
    let thumbPath: string | undefined;
    if (standbild) {
      thumbPath = tmpThumbPfad(kennung);
      await deps.schreibe(thumbPath, standbild).catch(() => {
        thumbPath = undefined;
      });
    }

    return {
      path: pfad,
      durationSeconds: dauerMs / 1000,
      extension: endung,
      segments: lauf.segmente,
      thumbPath,
    };
  } finally {
    bedienung.schliesse();
  }
}

/* ------------------------------------------------------------------ Die Bühne */

const KNOPF =
  'appearance:none;border:0;border-radius:10px;padding:10px 16px;font:600 15px system-ui;' +
  'background:#ffffff1a;color:#fff;cursor:pointer';

/**
 * Die Bühne aus reinem DOM.
 *
 * Kein React, kein Stilblatt: sie gehört zu `platform/` und darf nichts aus `features/` oder
 * `ui/` kennen. Die Beschriftungen stehen deutsch da — dieser Weg ist der Schreibtisch, nicht
 * die übersetzte Oberfläche des Prüfungstags.
 */
export function domBedienung(): Bedienung {
  let huelle: HTMLDivElement | null = null;
  let zeit: HTMLSpanElement | null = null;
  let haupt: HTMLButtonElement | null = null;

  return {
    oeffne({ mitTon, strom, horcher }) {
      huelle = document.createElement('div');
      // `pointer-events:auto`: ein offener Radix-Dialog setzt `none` auf `body` (2026-11-01).
      huelle.style.cssText =
        'position:fixed;inset:0;z-index:2000;background:#000;display:flex;pointer-events:auto;' +
        'flex-direction:column;align-items:center;justify-content:center;gap:16px';
      huelle.setAttribute('data-buehne', ''); // s. `ui/buehne.ts`

      const vorschau = document.createElement('video');
      vorschau.autoplay = true;
      vorschau.muted = true;
      vorschau.playsInline = true;
      vorschau.style.cssText = 'max-width:100%;max-height:70vh;background:#111';
      if (strom) vorschau.srcObject = strom;
      huelle.append(vorschau);

      zeit = document.createElement('span');
      zeit.style.cssText = 'color:#fff;font:600 17px system-ui;font-variant-numeric:tabular-nums';
      huelle.append(zeit);

      const leiste = document.createElement('div');
      leiste.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;justify-content:center';

      haupt = document.createElement('button');
      haupt.type = 'button';
      haupt.style.cssText = KNOPF;
      haupt.textContent = 'Aufnahme';
      let laeuft = false;
      haupt.onclick = () => {
        if (!laeuft && haupt?.dataset.begonnen !== 'ja') {
          haupt!.dataset.begonnen = 'ja';
          laeuft = true;
          horcher.start();
        } else if (laeuft) {
          laeuft = false;
          horcher.pause();
        } else {
          laeuft = true;
          horcher.weiter();
        }
      };

      const tonKnopf = document.createElement('button');
      tonKnopf.type = 'button';
      tonKnopf.style.cssText = KNOPF;
      let ton = mitTon;
      const tonBeschriften = () => (tonKnopf.textContent = ton ? 'Ton an' : 'Ton aus');
      tonBeschriften();
      tonKnopf.onclick = () => {
        ton = !ton;
        tonBeschriften();
        horcher.ton(ton);
      };

      const fertig = document.createElement('button');
      fertig.type = 'button';
      fertig.style.cssText = KNOPF;
      fertig.textContent = 'Fertig';
      fertig.onclick = () => horcher.fertig();

      const abbruch = document.createElement('button');
      abbruch.type = 'button';
      abbruch.style.cssText = KNOPF;
      abbruch.textContent = 'Abbrechen';
      abbruch.onclick = () => horcher.abbruch();

      leiste.append(haupt, tonKnopf, fertig, abbruch);
      huelle.append(leiste);
      document.body.append(huelle);
    },

    zeige(zustand, restMs) {
      if (zeit) {
        const s = Math.ceil(restMs / 1000);
        zeit.textContent =
          (zustand === 'pause' ? 'Pause · ' : zustand === 'laeuft' ? 'Aufnahme · ' : '') +
          'noch ' + String(Math.floor(s / 60)) + ':' + String(s % 60).padStart(2, '0');
      }
      if (haupt) haupt.textContent = zustand === 'laeuft' ? 'Pause' : zustand === 'pause' ? 'Weiter' : 'Aufnahme';
    },

    schliesse() {
      huelle?.remove();
      huelle = null;
      zeit = null;
      haupt = null;
    },
  };
}

/* ------------------------------------------------------------------ Das Plugin */

export function baueVideoWebDeps(
  deps: VideoWebDeps,
  vorschauZeit: (durationSeconds: number) => number,
): VideoWebTeile {
  return {
    vorschauZeit,
    quelle: deps.quelle ?? browserQuelle,
    bedienung: deps.bedienung ?? domBedienung,
    schreibe:
      deps.schreibe ??
      ((pfad, inhalt) => schreibeBase64(pfad, inhalt, { ordnerAnlegen: true })),
    standbild: deps.standbild ?? browserStandbild,
    jetzt: deps.jetzt ?? (() => Date.now()),
    taktMs: deps.taktMs ?? 250,
    kennung: deps.kennung ?? (() => crypto.randomUUID()),
  };
}
